/**
 * shared/attachmentsPanel/useAttachmentLabels.js - linking a file as THE
 * file a record's number opens, and fixing a card's label.
 * Moved verbatim out of AttachmentsPanel.jsx; the panel passes in the
 * values it reads.
 */
import { useState, useCallback } from 'react';
import { REF_OPTIONAL } from '../../../utils/attachmentPreview';
import { notifyDocsChanged } from '../../../hooks/useDocIndex';

export function useAttachmentLabels({ base, load, onChanged }) {
  /* Link: make one file THE file this record's number opens (Quotes, PO) or
     THE RFQ for its quote (RFQ emails). The server unlinks whichever file held
     the spot, in one transaction. A quote/PO link also sets the file's label to
     the record's exact number, so the table link is guaranteed to find it. */
  const [linking, setLinking] = useState(null); // attachment id being saved
  const setLink = useCallback(
    async (f, body) => {
      setLinking(f.id);
      try {
        const r = await fetch(`${base}/attachments/${encodeURIComponent(f.id)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (r.ok) {
          await load();
          notifyDocsChanged();
          if (onChanged) onChanged();
        } else {
          const j = await r.json().catch(() => null);
          alert(j?.error || 'Could not change the link.');
        }
      } catch {
        alert('Could not change the link - check your connection.');
      }
      setLinking(null);
    },
    [load, onChanged, base]
  );

  // Per-card label editor — fix a wrong or missing Invoice/PO/Quote tag.
  const [editing, setEditing] = useState(null); // { id, doc_type, doc_ref, error }
  const saveLabel = useCallback(async () => {
    if (!editing) return;
    const needs = editing.doc_type && !REF_OPTIONAL.has(editing.doc_type);
    if (!editing.doc_type || (needs && !String(editing.doc_ref || '').trim())) {
      setEditing((e) => ({ ...e, error: 'Pick a type and enter its number.' }));
      return;
    }
    try {
      const r = await fetch(`${base}/attachments/${encodeURIComponent(editing.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc_type: editing.doc_type, doc_ref: String(editing.doc_ref || '').trim() }),
      });
      if (r.ok) {
        setEditing(null);
        await load();
        notifyDocsChanged();
        if (onChanged) onChanged();
      } else {
        const j = await r.json().catch(() => null);
        setEditing((e) => ({ ...e, error: j?.error || 'Save failed.' }));
      }
    } catch {
      setEditing((e) => ({ ...e, error: 'Save failed - check your connection.' }));
    }
  }, [editing, load, onChanged, base]);
  return { editing, setEditing, saveLabel, linking, setLink };
}
